import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import React, { useState } from 'react';
import { useGetStressAssessmentSchedule } from '~/usecases/useGetStressAssessmentSchedule';
import { ROUTES } from '~/utils/routes';
import type { MonthOption, MonthRow } from '../types';
import { usePostStressAssessmentSchedule } from './usePostStressAssessmentSchedule';

type IUseStressAssessmentSchedule = () => {
  state: {
    rows: MonthRow[];
    isSaveLoading: boolean;
  };
  actions: {
    onChangeMonth: (id: string, month: MonthOption | null) => void;
    onAddMonth: () => void;
    onRemoveMonth: (id: string) => void;
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    onCancel: () => void;
  };
};

/**
 * ストレスチェックスケジュール設定の状態管理とアクションを提供する
 */
export const useStressAssessmentSchedule: IUseStressAssessmentSchedule = () => {
  const threeLetterCode = getThreeLetterCode();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data } = useGetStressAssessmentSchedule({ threeLetterCode });

  const buildInitialRows = (): MonthRow[] => {
    const targetMonths = (data?.stress_check_schedule?.target_months ?? []).filter(
      (m): m is MonthOption => m >= 1 && m <= 12
    );
    if (targetMonths.length > 0) {
      return targetMonths.map(month => ({ id: crypto.randomUUID(), month }));
    }
    return [{ id: crypto.randomUUID(), month: null }];
  };

  const [rows, setRows] = useState<MonthRow[]>(buildInitialRows);

  const { trigger, isMutating } = usePostStressAssessmentSchedule(threeLetterCode, {
    swr: {
      onSuccess: () => {
        showToast({ message: 'スケジュールを保存しました。', status: 'success' });
        navigate(ROUTES.STRESS_ASSESSMENTS.INDEX);
      },
      onError: () => {
        showToast({ message: 'スケジュールの保存に失敗しました。', status: 'danger' });
      },
    },
  });

  /**
   * 指定行の月を変更する
   */
  const onChangeMonth = (id: string, month: MonthOption | null) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, month } : row)));
  };

  /**
   * 新しい実施月行を追加する
   */
  const onAddMonth = () => {
    setRows(prev => [...prev, { id: crypto.randomUUID(), month: null }]);
  };

  /**
   * 指定行を削除する
   */
  const onRemoveMonth = (id: string) => {
    setRows(prev => {
      const next = prev.filter(row => row.id !== id);
      return next.length > 0 ? next : [{ id: crypto.randomUUID(), month: null }];
    });
  };

  /**
   * スケジュールを保存する
   */
  const onSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    const months = [...new Set(rows.flatMap(row => (row.month !== null ? [row.month] : [])))];
    trigger({ target_months: months });
  };

  /**
   * スケジュール設定をキャンセルしてストレスアセスメント一覧に戻る
   */
  const onCancel = () => {
    navigate(ROUTES.STRESS_ASSESSMENTS.INDEX);
  };

  return {
    state: {
      rows,
      isSaveLoading: isMutating,
    },
    actions: {
      onChangeMonth,
      onAddMonth,
      onRemoveMonth,
      onSubmit,
      onCancel,
    },
  };
};
